import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import {
  Button,
  ComponentVersions_3_0,
  ComponentVersionsInherit,
  ConnectIcon,
  DisconnectIcon,
  Grid,
  IconButton,
  InformationCircleOutlinedIcon,
  Skeleton,
  styled,
  Tooltip,
  Typography,
  useTheme,
} from 'opensolar-ui'
import { useGetSupplierConfig } from 'pages/ordering/hooks/useGetSupplierConfig'
import { useTranslate } from 'ra-core'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { matchPath, useHistory, useLocation } from 'react-router-dom'
import { ActionType } from 'types/tracking'
import { useFeatureFlag } from 'util/featureFlags'
import { HardwareSupplierFilterKeyType } from '../../pages/ordering/type'
import { ExtendedHardwareSupplierConfig } from '../../projectSections/sections/manage3/hardware/DistributorChip'
import useGetDistributorConnectionStatusAndConfig from '../../projectSections/sections/manage3/hardware/hooks/useGetDistributorConnectionStatusAndConfig'
import { SelectedMenu } from './constant'
import SideBarNavigationSubMenu from './SideBarNavigationSubMenu'
import { MenuConfigType } from './type'
import { useSidebarMenuConfig } from './useSidebarMenuConfig'

const updateBuyHardwareSubMenuUrls = (
  config: MenuConfigType[],
  distributor: HardwareSupplierFilterKeyType
): MenuConfigType[] => {
  const homePath = distributor ? `/shop/supplier/${distributor}` : '/shop/'

  return config.map((item) => {
    if (item.type === SelectedMenu.BUY_HARDWARE && item.subMenu) {
      return {
        ...item,
        subMenu: item.subMenu.map((subItem) => ({
          ...subItem,
          url: subItem.key === 'home' ? homePath : `/shop/search/${subItem.key}/supplier/${distributor}`,
        })),
      }
    }
    return item
  })
}

const GridSpace = styled(Grid)(({ theme }) => ({
  padding: '5px 0',
}))

const ButtonNoPadding = styled(Button)(({ theme }) => ({
  padding: '0 2px',
}))

const DistributorSectionWrapper = styled('div')<{ shouldAnimate: boolean }>(({ shouldAnimate }) => ({
  overflow: 'hidden',
  opacity: shouldAnimate ? 0 : 1,
  transform: shouldAnimate ? 'translateY(-10px)' : 'translateY(0)',
  animation: shouldAnimate ? 'fadeInSlideDown 0.3s ease-in-out forwards' : 'none',
  '@keyframes fadeInSlideDown': {
    '0%': {
      opacity: 0,
      transform: 'translateY(-10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}))

const DistributorConnectStatusMenuTitleContainer = styled('div')({
  padding: '8px 0px',
  display: 'flex',
  gap: '10px',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'center',
})

const DistributorConnectContentContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  width: '100%',
})

const ConnectionChipButton = styled(IconButton)({
  width: 'fit-content',
  height: 'fit-content',
})

interface ConnectionChipProps {
  isConnected: boolean
  filterKey: HardwareSupplierFilterKeyType
  onClickConnected?: () => void
  trackUserInteraction?: () => void
}

export const ConnectionChip = ({
  isConnected,
  filterKey,
  onClickConnected,
  trackUserInteraction,
}: ConnectionChipProps) => {
  const history = useHistory()
  const translate = useTranslate()

  const onClickConnect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    trackUserInteraction?.()

    if (isConnected) {
      onClickConnected?.()
      return
    }

    e.stopPropagation()

    history.push('/shop_suppliers', { ...(filterKey === 'outlet' ? {} : { filterKey }) })
  }, [])

  return (
    <Tooltip
      placement="right"
      arrow={false}
      message={translate(isConnected ? 'Supplier connected' : 'Click to connect your account and unlock trade pricing')}
      PopperProps={{
        sx: {
          maxWidth: 189,
        },
      }}
    >
      <ConnectionChipButton onClick={onClickConnect}>
        {isConnected ? <ConnectIcon /> : <DisconnectIcon />}
      </ConnectionChipButton>
    </Tooltip>
  )
}

interface DistributorStatusMenusProps {
  onClickDistributor: (config: ExtendedHardwareSupplierConfig) => void
}

const DistributorConnectStatusMenus = ({ onClickDistributor }: DistributorStatusMenusProps) => {
  const theme = useTheme()
  const translate = useTranslate()
  const { distributorConfigs, isChecking } = useGetDistributorConnectionStatusAndConfig(undefined)
  const hasAnimatedRef = useRef(false)
  const shouldAnimate = !hasAnimatedRef.current

  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'side_bar_navigation_menus',
    eventName: 'Hardware Navigation Button',
  })

  const sortedDistributors = useMemo(() => {
    if (!distributorConfigs) return []

    const enableDistributor = distributorConfigs.filter((distributorConfigs) => distributorConfigs.isEnabled)

    return [...enableDistributor].sort((a, b) => {
      if (a.isConnected === b.isConnected) return 0
      return a.isConnected ? -1 : 1
    })
  }, [distributorConfigs])

  useEffect(() => {
    if (!hasAnimatedRef.current) {
      hasAnimatedRef.current = true
    }
  }, [])

  return (
    <ComponentVersionsInherit versions={{ chip: 2, button: 2 }}>
      {sortedDistributors && sortedDistributors.length > 1 && (
        <DistributorSectionWrapper shouldAnimate={shouldAnimate}>
          <Grid container>
            <DistributorConnectStatusMenuTitleContainer>
              <Typography
                textVariant="caption1Bold"
                style={{ whiteSpace: 'nowrap' }}
                colorHex={theme.palette.neutralGrey[900]}
              >
                {translate('SHOP BY SUPPLIER')}
              </Typography>

              <Tooltip
                placement="right"
                arrow={false}
                message={translate(
                  'Connect your OpenSolar organisation to supported suppliers in Control to unlock real-time pricing and a faster, smoother checkout experience'
                )}
              >
                <div>
                  <InformationCircleOutlinedIcon />
                </div>
              </Tooltip>
            </DistributorConnectStatusMenuTitleContainer>
            <DistributorConnectContentContainer>
              {sortedDistributors?.map((config) => (
                <ButtonNoPadding key={config.filterKey} size="small" fullWidth>
                  <GridSpace
                    container
                    justify="space-between"
                    alignItems="center"
                    key={config.filterKey}
                    onClick={() => onClickDistributor(config)}
                    style={{
                      padding: '0px 8px 0px 12px',
                      width: '100%',
                    }}
                  >
                    <Grid
                      item
                      xs
                      zeroMinWidth
                      style={{
                        textAlign: 'left',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isChecking ? <></> : config.name}
                      </Typography>
                    </Grid>
                    <Grid
                      item
                      style={{
                        marginLeft: '8px',
                        flexShrink: 0,
                      }}
                    >
                      {isChecking ? (
                        <Skeleton width={70} height={24} />
                      ) : (
                        <ConnectionChip
                          isConnected={config.isConnected}
                          filterKey={config.filterKey}
                          trackUserInteraction={() =>
                            trackUserInteraction(
                              { type: ActionType.CLICK },
                              {
                                source: 'lhs_shop_by_distributor',
                                distributor: config.filterKey,
                                context: 'connect_button',
                              }
                            )
                          }
                        />
                      )}
                    </Grid>
                  </GridSpace>
                </ButtonNoPadding>
              ))}
            </DistributorConnectContentContainer>
          </Grid>
        </DistributorSectionWrapper>
      )}
    </ComponentVersionsInherit>
  )
}

const getSelectedMenuByRoute = (pathName: string, menuConfig: MenuConfigType[]) => {
  return menuConfig.find((menu) => new RegExp(menu.routeRule).test(pathName))
}

const SideBarNavigationMenusContainer = styled('div')<{ fitParentHeight?: boolean }>(({ fitParentHeight = false }) => ({
  ...(fitParentHeight ? { height: '100%', maxHeight: '100%' } : { maxHeight: '100vh' }),
  scrollbarColor: 'transparent transparent',
  scrollbarWidth: 'thin',
  backgroundColor: 'white',
  overflow: 'auto',
  overflowY: 'auto',
  boxSizing: 'border-box',
  '&::-webkit-scrollbar': {
    width: '0px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'transparent',
    borderRadius: '4px',
  },
  '&:not(:hover)': {
    scrollbarColor: 'transparent transparent',
  },
  '&:hover': {
    scrollbarColor: 'grey transparent',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#888',
    },
  },
  pointerEvents: 'auto',
}))

const MainMenuContainer = styled('div')<{ fitParentHeight?: boolean }>(({ theme, fitParentHeight }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '15px',
  padding: `${fitParentHeight ? 16 : 24}px 10px`,
  background: theme.palette.neutralGrey[200],
}))

const SubMenuContainer = styled('div')({
  padding: '8px 10px 16px 10px',
  backgroundColor: 'white',
})

const MenuButton = styled(Button)<{ isActive: boolean; isCollapsed?: boolean }>(({ theme, isActive, isCollapsed }) => ({
  display: 'flex',
  flexDirection: isCollapsed ? 'column' : 'row',
  justifyContent: isCollapsed ? 'center' : 'flex-start',
  alignItems: 'center',
  width: isCollapsed ? '52px' : '100%',
  height: isCollapsed ? '40px' : 'auto',
  gap: isCollapsed ? '4px' : '15px',
  border: `1px solid ${theme.palette.neutralGrey[500]}`,
  borderRadius: '6px',
  padding: isCollapsed ? '8px' : '8px 16px',
  minHeight: isCollapsed ? '40px' : 'auto',
  whiteSpace: 'nowrap',
  background: isActive ? `${theme.palette.blue[800]} !important` : `${theme.palette.neutralGrey[100]} !important`,
  color: isActive ? `${theme.palette.white} !important` : `${theme.palette.neutralGrey[1000]} !important`,
  '&:hover': {
    border: `1px solid ${theme.palette.blue[800]} !important`,
    background: `${theme.palette.blue[800]} !important`,
    color: `${theme.palette.white} !important`,
    '& .MuiSvgIcon-root, & .OSUI-Typography-root': {
      color: `${theme.palette.white} !important`,
    },
  },
  '& .MuiSvgIcon-root, & .OSUI-Typography-root': {
    color: isActive ? `${theme.palette.white} !important` : `${theme.palette.neutralGrey[1000]} !important`,
  },
}))

interface SideBarNavigationMenusProps {
  isCollapsed?: boolean
  // Only true when rendered inside the shop_uplift hover sidebar, whose
  // fixed-position panel already constrains the available height.
  fitParentHeight?: boolean
}

const SideBarNavigationMenus = ({ isCollapsed = false, fitParentHeight = false }: SideBarNavigationMenusProps) => {
  const history = useHistory()
  const theme = useTheme()
  const translate = useTranslate()
  const location = useLocation()

  const { pathname } = location
  const routeSupplierMatch = matchPath<{ distributor: string }>(pathname, {
    path: ['/shop/supplier/:distributor', '/shop/search/:searchKey/supplier/:distributor'],
  })
  const routeDistributor = routeSupplierMatch?.params?.distributor as HardwareSupplierFilterKeyType | undefined

  const enableInventoryManagement = useFeatureFlag('inventory_management', 'on')
  const enableMultiDistributorShop = useFeatureFlag('enable_multi_distributor_shop', 'on')
  const enableMultiProjectOrdering = useFeatureFlag('shop_multi_project_ordering', 'on')
  const enableQuoteReview = useFeatureFlag('hardware_3_0_review_quote_widget', 'on')

  const [selectedMenu, setSelected] = useState<MenuConfigType | undefined>(undefined)
  const [selectedDistributor, updateDistributor] = useState<HardwareSupplierFilterKeyType>()
  const [menuConfig, setMenuConfig] = useState<MenuConfigType[]>([])
  const [visits, setVisits] = useState<number>(0)
  const activeDistributor = routeDistributor ?? selectedDistributor
  const baseSidebarMenuConfig = useSidebarMenuConfig(activeDistributor)
  const { trackUserInteraction } = useTrackComponent({
    componentKey: 'side_bar_navigation_menus',
    eventName: 'Hardware Navigation Button',
  })

  const { getSupplierConfig } = useGetSupplierConfig()

  useEffect(() => {
    if (baseSidebarMenuConfig.length > 0 && !selectedMenu) {
      const menuForCurrentRoute = getSelectedMenuByRoute(pathname, baseSidebarMenuConfig)
      setMenuConfig(baseSidebarMenuConfig)
      setSelected(menuForCurrentRoute || baseSidebarMenuConfig[0])
    }
  }, [baseSidebarMenuConfig, pathname, selectedMenu])

  useEffect(() => {
    if (activeDistributor && baseSidebarMenuConfig.length > 0) {
      const newConfig = updateBuyHardwareSubMenuUrls(baseSidebarMenuConfig, activeDistributor)
      setMenuConfig(newConfig)
      const menuWithDistributorUrls = newConfig.find((newMenu) => newMenu.type === selectedMenu?.type)
      if (menuWithDistributorUrls) setSelected(menuWithDistributorUrls)
    }
  }, [activeDistributor, baseSidebarMenuConfig, selectedMenu?.type])

  useEffect(() => {
    const distributorFromState = (location.state as { distributor?: string })?.distributor
    if (distributorFromState) {
      updateDistributor(distributorFromState as HardwareSupplierFilterKeyType)
    }
  }, [location.state])

  useEffect(() => {
    if (baseSidebarMenuConfig.length === 0) return

    if (location.pathname === '/shop') {
      setMenuConfig([...baseSidebarMenuConfig])
      setSelected(baseSidebarMenuConfig[0])
      updateDistributor(undefined)

      if (location.pathname.includes('after_connect')) {
        setVisits(visits + 1)
      }
      return
    }

    if (activeDistributor) {
      let menu = getSelectedMenuByRoute(location.pathname, baseSidebarMenuConfig)
      const newConfig = updateBuyHardwareSubMenuUrls(baseSidebarMenuConfig, activeDistributor)
      const menuWithDistributorUrls = newConfig.find((newMenu) => newMenu.type === menu?.type)
      if (menuWithDistributorUrls) setSelected(menuWithDistributorUrls)
    }
  }, [location.pathname, baseSidebarMenuConfig, activeDistributor])

  const onChange = (menu: MenuConfigType) => {
    updateDistributor(undefined)
    history.push(menu.redirectUrl)
    setSelected(menu)

    const amplitudeEventName = enableMultiDistributorShop
      ? 'Hardware Navigation Button'
      : 'Hardware Sidebar Navigation Button Clicked'
    const amplitudeEventSource = enableMultiDistributorShop ? menu?.amplitudeEventSourceV2 : menu?.amplitudeEventSource

    trackUserInteraction({ type: ActionType.SELECT }, { eventName: amplitudeEventName, source: amplitudeEventSource })
  }

  const onSelectDistributorMenu = useCallback(async (config: ExtendedHardwareSupplierConfig) => {
    trackUserInteraction(
      { type: ActionType.SELECT },
      { source: 'lhs_shop_by_distributor', distributor: config.filterKey }
    )
    updateDistributor(config.filterKey)
    history.push(`/shop/supplier/${config.filterKey}`)
  }, [])

  const distributorName: string | undefined = activeDistributor
    ? getSupplierConfig(activeDistributor)?.name
    : undefined

  if (!selectedMenu || menuConfig.length === 0) {
    return null
  }

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <SideBarNavigationMenusContainer
        fitParentHeight={fitParentHeight}
        style={{ overflowX: 'hidden' }}
        data-sidebar-scroll-container
      >
        <MainMenuContainer fitParentHeight={fitParentHeight}>
          {menuConfig.map((menu) => {
            if (menu.type === SelectedMenu.INVENTORY_MANAGEMENT && !enableInventoryManagement) {
              return null
            }
            if (menu.type === SelectedMenu.QUOTES && !enableQuoteReview) {
              return null
            }
            if (menu.type === SelectedMenu.ORDER_BY_PROJECT && !enableMultiProjectOrdering) {
              return null
            }

            const menuButton = (
              <MenuButton
                key={menu.type}
                fullWidth
                onClick={() => onChange(menu)}
                startIcon={isCollapsed ? undefined : menu.icon}
                isActive={selectedMenu?.type === menu.type}
                isCollapsed={isCollapsed}
                style={{ whiteSpace: 'nowrap' }}
              >
                {isCollapsed ? menu.icon : <Typography textVariant="body1">{translate(menu.buttonLabel)}</Typography>}
              </MenuButton>
            )

            return (
              <div key={menu.type}>
                {isCollapsed ? (
                  <Tooltip placement="right" arrow={false} message={translate(menu.buttonLabel)}>
                    {menuButton}
                  </Tooltip>
                ) : (
                  menuButton
                )}

                {!isCollapsed &&
                  enableMultiDistributorShop &&
                  selectedMenu.type === SelectedMenu.BUY_HARDWARE &&
                  menu.type === SelectedMenu.BUY_HARDWARE && (
                    <DistributorConnectStatusMenus
                      key={`connection-menus-${visits}`}
                      onClickDistributor={onSelectDistributorMenu}
                    />
                  )}
              </div>
            )
          })}
        </MainMenuContainer>

        <SubMenuContainer style={{ display: isCollapsed ? 'none' : 'block' }}>
          <SideBarNavigationSubMenu
            title={distributorName ? `${translate('Shop')} ${distributorName}` : translate(selectedMenu.buttonLabel)}
            subMenus={selectedMenu.subMenu}
            activeMenu={selectedMenu.type}
            distributorName={distributorName}
          />
        </SubMenuContainer>
      </SideBarNavigationMenusContainer>
    </ComponentVersionsInherit>
  )
}

export default SideBarNavigationMenus
